import React, { createContext, useContext, useEffect, useState } from "react";
import { calculateServerTimeOffset } from "@/utils/timezoneUtils";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, RefreshCw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ServerTimeContextType {
  offset: number | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

const ServerTimeContext = createContext<ServerTimeContextType | undefined>(
  undefined
);

interface ServerTimeProviderProps {
  children: React.ReactNode;
}

export const ServerTimeProvider: React.FC<ServerTimeProviderProps> = ({
  children,
}) => {
  const [offset, setOffset] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchServerTimeOffset = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const calculatedOffset = await calculateServerTimeOffset();
      setOffset(calculatedOffset);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      setError(errorMessage);
      console.error("Failed to calculate server time offset:", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefetch = () => {
    fetchServerTimeOffset();
  };

  useEffect(() => {
    fetchServerTimeOffset();
  }, []);

  // Show loading state during initial fetch
  if (isLoading && !offset) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <Clock className="h-12 w-12 text-blue-500 mx-auto animate-pulse" />
              <h3 className="text-lg font-semibold text-blue-600">
                Validating time...
              </h3>
              {/* <p className="text-gray-600">
                Connecting to server to ensure accurate timing...
              </p> */}
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state if time sync failed
  if (error && !offset) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto" />
              <h3 className="text-lg font-semibold text-red-600">
                Time Sync Failed
              </h3>
              <p className="text-gray-600">
                Unable to validate time with server time. This is required for
                assessment integrity.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <Button
                onClick={handleRefetch}
                disabled={isLoading}
                className="mt-4"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Try Again
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-500 mt-2">
                Please check your internet connection and try again. If the
                problem persists, contact support.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <ServerTimeContext.Provider
      value={{
        offset,
        isLoading,
        error,
        refetch: handleRefetch,
      }}
    >
      {children}
    </ServerTimeContext.Provider>
  );
};

export const useServerTime = (): ServerTimeContextType => {
  const context = useContext(ServerTimeContext);
  if (context === undefined) {
    throw new Error("useServerTime must be used within a ServerTimeProvider");
  }
  return context;
};

/**
 * Hook to get current server time using the global offset
 */
export const useCurrentServerTime = () => {
  const { offset } = useServerTime();
  const [currentTime, setCurrentTime] = useState<Date | null>(null);

  useEffect(() => {
    if (offset === null) return;

    const updateTime = () => {
      setCurrentTime(new Date(Date.now() + offset));
    };

    // Update immediately
    updateTime();

    // Update every second
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [offset]);

  return currentTime;
};
